// WP-1.1 (S-12 `/accounts/:id`): balance header; Reconcile (S-13) · Edit · Archive
// (disabled at a non-zero balance, with a tooltip explaining why). Transactions table
// (AC-6) lands with WP-1.2.
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { AccountForm } from '../components/AccountForm';
import { ghostButtonInlineClass, primaryButtonInlineClass } from '../components/FormKit';
import { ApiError } from '../lib/api';
import { useAccount, useArchiveAccount, useUnarchiveAccount } from '../lib/accounts';

export function AccountDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: account, isLoading, isError } = useAccount(id);
  const [editOpen, setEditOpen] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const archiveMutation = useArchiveAccount(id);
  const unarchiveMutation = useUnarchiveAccount(id);

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (isError || account === undefined) {
    return <p className="text-sm text-muted">Account not found.</p>;
  }

  const canArchive = account.balanceMinor === 0;

  return (
    <div>
      <button
        onClick={() => {
          navigate('/accounts');
        }}
        className="mb-4 flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft size={16} /> Accounts
      </button>

      <div className="mb-6 rounded-[var(--radius-card)] border border-border bg-surface p-6">
        <p className="text-sm text-muted">{account.name}</p>
        <Amount
          currency={account.currency}
          minorUnits={account.balanceMinor}
          className="text-3xl font-semibold"
        />
        <p className="mt-1 text-xs text-muted">
          {account.type} {account.platformGroup !== undefined && `· ${account.platformGroup}`}
          {account.archived && ' · Archived'}
        </p>

        <div className="mt-4 flex gap-2">
          <button
            className={ghostButtonInlineClass}
            onClick={() => {
              navigate(`/accounts/${id}/reconcile`);
            }}
          >
            Reconcile
          </button>
          <button
            className={ghostButtonInlineClass}
            onClick={() => {
              setEditOpen(true);
            }}
          >
            Edit
          </button>
          {account.archived ? (
            <button
              className={primaryButtonInlineClass}
              onClick={() => {
                unarchiveMutation.mutate();
              }}
              disabled={unarchiveMutation.isPending}
            >
              Unarchive
            </button>
          ) : (
            <button
              className={ghostButtonInlineClass}
              title={canArchive ? undefined : 'Archive is only allowed at a zero balance'}
              disabled={!canArchive || archiveMutation.isPending}
              onClick={() => {
                setArchiveError(null);
                archiveMutation.mutate(undefined, {
                  onError: (e) => {
                    setArchiveError(e instanceof ApiError ? e.message : 'Could not archive');
                  },
                });
              }}
            >
              Archive
            </button>
          )}
        </div>
        {archiveError !== null && <p className="mt-2 text-sm text-danger">{archiveError}</p>}
      </div>

      <AccountForm open={editOpen} onOpenChange={setEditOpen} account={account} />
    </div>
  );
}
